/**
 * sanitize.js
 *
 * Centralised XSS sanitization utilities.
 *
 * Two exported functions:
 *
 * 1. sanitizeInput(value)
 *    - Strips complete HTML tags from a string using a regex, but preserves:
 *        • All inner text content between tags
 *        • Standalone < or > characters that are NOT part of a complete tag
 *        • All normal text: letters, numbers, spaces, punctuation, @, ., -, $, etc.
 *    - Does NOT use DOMPurify (which HTML-encodes < to < causing field display issues).
 *    - Users can freely type AND paste any text — the field displays exactly what
 *      they typed, including individual < and > characters.
 *    - When a COMPLETE HTML tag is detected (opening <...> or closing </...>),
 *      the tag itself is stripped but the text between tags is kept.
 *
 *    Examples:
 *      "<script>alert(1)</script>"   →  "alert(1)"
 *      "<b>bold text</b>"            →  "bold text"
 *      "<img src=x onerror=alert()>" →  ""  (void tag, no inner text)
 *      "5 < 10 and 3 > 2"           →  "5 < 10 and 3 > 2"  (standalone < > preserved)
 *      "user@email.com"              →  "user@email.com"  (unchanged)
 *      "Hello, World! $100.00"       →  "Hello, World! $100.00"  (unchanged)
 *
 *    Use on every free-text input field's onChange handler before storing in state.
 *
 * 2. sanitizeHtml(html)
 *    - Sanitizes server-provided HTML using DOMPurify for safe rendering via
 *      dangerouslySetInnerHTML.
 *    - Keeps safe display tags (<p>, <b>, <ul>, <li>, <br>, etc.) but strips
 *      <script>, event handlers (onclick, onerror), javascript: URLs, and all
 *      other XSS vectors.
 *    - Use ONLY for rendering backend/CMS HTML — never for user input fields.
 */

import DOMPurify from 'dompurify';

/**
 * Strips complete HTML tags from user input while preserving inner text and
 * standalone angle bracket characters that are not part of a valid tag.
 *
 * Uses a simple regex: /<[^>]+>/g
 *   - Matches: < followed by one or more non-> characters, then >
 *   - Does NOT match: standalone < with no closing >
 *
 * @param {string} value - Raw value from an input field (typed or pasted)
 * @returns {string} Value with complete HTML tags removed; inner text and normal chars preserved
 */
export const sanitizeInput = (value) => {
  if (typeof value !== 'string') return value;
  // Remove complete HTML tags (e.g. <script>, </script>, <img src=x onerror=...>)
  // Preserve all text content between tags and standalone < > characters
  return value.replace(/<[^>]+>/g, '');
};

/**
 * Sanitizes server-provided HTML for safe rendering via dangerouslySetInnerHTML.
 * Removes <script>, event handlers, javascript: URLs, and other XSS vectors.
 * Preserves legitimate display HTML (formatting, links with safe hrefs, etc.).
 *
 * @param {string} html - Raw HTML string from backend/CMS
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = (html) => {
  if (typeof html !== 'string') return '';
  return DOMPurify.sanitize(html);
};
